import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

const controlSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' },
};

const FacetSelect = ({ label, options, value, onChange, minWidth = 190 }) => {
  if (!options.length) return null;
  return (
    <FormControl size="small" sx={{ ...controlSx, minWidth }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(event) => onChange(event.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) =>
          (selected.length === 1 ? selected[0] : `${selected.length} selected`)}
        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} dense>
            <Checkbox size="small" checked={value.includes(option.value)} />
            <ListItemText primary={option.value} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
              {option.count.toLocaleString()}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const ColumnsMenu = ({ columns, visibleColumns, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const toggle = (columnId) => {
    const next = visibleColumns.includes(columnId)
      ? visibleColumns.filter((id) => id !== columnId)
      : [...visibleColumns, columnId];
    // The sticky first column anchors the row, so it can never be hidden.
    if (!next.length) return;
    onChange(columns.filter((column) => next.includes(column.id)).map((column) => column.id));
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ViewColumnIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
      >
        Columns ({visibleColumns.length})
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { maxHeight: 420 } }}
      >
        {columns.map((column) => (
          <MenuItem
            key={column.id}
            dense
            disabled={column.sticky}
            onClick={() => toggle(column.id)}
          >
            <Checkbox size="small" checked={visibleColumns.includes(column.id)} />
            <ListItemText primary={column.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ReportToolbar = ({
  mode,
  search,
  onSearchChange,
  facetIndex,
  facets,
  onFacetChange,
  groupBy,
  groupOptions,
  onGroupByChange,
  density,
  onDensityChange,
  columns,
  visibleColumns,
  onVisibleColumnsChange,
  shownCount,
  totalCount,
  onCopyVisible,
  onClearView,
  hasActiveView,
  copyState,
}) => {
  const [text, setText] = useState(search);
  const latestOnSearchChange = useRef(onSearchChange);
  latestOnSearchChange.current = onSearchChange;

  useEffect(() => {
    setText(search);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => latestOnSearchChange.current(text), 220);
    return () => clearTimeout(timer);
  }, [text]);

  return (
    <Stack spacing={1.25} sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Search title, number, law area, owner..."
          sx={{ ...controlSx, flex: '1 1 280px', minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: text ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setText('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <FacetSelect
          label="Law area"
          options={facetIndex.lawAreas}
          value={facets.lawAreas}
          onChange={(value) => onFacetChange('lawAreas', value)}
        />
        <FacetSelect
          label="Criticality"
          options={facetIndex.criticalities}
          value={facets.criticalities}
          onChange={(value) => onFacetChange('criticalities', value)}
          minWidth={160}
        />
        <FacetSelect
          label="Department"
          options={facetIndex.departments}
          value={facets.departments}
          onChange={(value) => onFacetChange('departments', value)}
        />
        {mode === 'trackerDueDate' && (
          <FacetSelect
            label="Status"
            options={facetIndex.statuses}
            value={facets.statuses}
            onChange={(value) => onFacetChange('statuses', value)}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <FormControl size="small" sx={{ ...controlSx, minWidth: 170 }}>
          <InputLabel>Group by</InputLabel>
          <Select
            value={groupBy}
            label="Group by"
            onChange={(event) => onGroupByChange(event.target.value)}
          >
            {groupOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={density}
          onChange={(_event, value) => value && onDensityChange(value)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.5 } }}
        >
          <ToggleButton value="compact">Compact</ToggleButton>
          <ToggleButton value="comfortable">Comfortable</ToggleButton>
        </ToggleButtonGroup>

        <ColumnsMenu
          columns={columns}
          visibleColumns={visibleColumns}
          onChange={onVisibleColumnsChange}
        />

        <Tooltip title="Copy the rows and columns currently on screen as tab-separated text, ready to paste into Excel">
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={onCopyVisible}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            {copyState === 'copied' ? 'Copied' : 'Copy visible rows'}
          </Button>
        </Tooltip>

        {hasActiveView && (
          <Button
            size="small"
            color="inherit"
            startIcon={<FilterAltOffIcon />}
            onClick={onClearView}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Clear view
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <Chip
          size="small"
          label={`Showing ${shownCount.toLocaleString()} of ${totalCount.toLocaleString()}`}
          color={shownCount === totalCount ? 'default' : 'success'}
          variant={shownCount === totalCount ? 'outlined' : 'filled'}
          sx={{ fontWeight: 700 }}
        />
      </Stack>
    </Stack>
  );
};

export default ReportToolbar;
